/**
 * Phase 1N (Award Engine Integrity 2026-05-17) — allowance code canonical
 * bidirectional handler.
 *
 * SINGLE SOURCE OF TRUTH for translating historical allowance codes (emitted
 * by engines pre-Phase-1N) into canonical _ALLOWANCE-suffixed names (emitted
 * by engines post-Phase-1N). This module is the only place that holds the
 * old → canonical mapping; every consumer reading
 * `pay_run_items.allowance_lines_snapshot[*].code` OR
 * `timesheets.allowances_breakdown[*].code` MUST import `canonicalAllowanceCode`
 * rather than inline-switching on codes.
 *
 * Per the Phase 1N audit-correct date-cutover policy:
 *  - Pre-Phase-1N historical rows are NEVER rewritten (they hold the truth of
 *    how a past pay run was computed). They carry old codes (e.g.,
 *    'SLEEPOVER', 'BROKEN_SHIFT', 'DELAYED_MEAL', 'TRAVEL_KM',
 *    'UNIFORM_PER_SHIFT', 'ON_CALL_WD', 'SCHADS_SLEEPOVER_ALLOW', etc.).
 *  - Post-Phase-1N rows carry canonical _ALLOWANCE-suffixed names emitted
 *    directly by the engines (e.g., 'SLEEPOVER_ALLOWANCE',
 *    'BROKEN_SHIFT_SINGLE_ALLOWANCE', 'MEAL_ALLOWANCE',
 *    'NAUSEOUS_WORK_ALLOWANCE', etc.).
 *
 * Consumers iterate allowance lines + canonicalize each code via
 * `canonicalAllowanceCode(rawCode)` before downstream lookup. The Xero
 * batch-builder's `ALLOWANCE_CODE_MAP` is keyed by canonical names; the
 * canonicalizer normalises historical reads.
 *
 * Source of truth deprecated-codes list:
 * `~/.claude/projects/-Users-paulbenson-StaturaCare-statura-agedcare/memory/project_allowance_code_naming_taxonomy.md`.
 *
 * Verification artifacts:
 *  - `docs/operations/clause-verifications/2026-05-17-aged-care-legislation-schads-broken-shift-clause-ref.md`
 *  - `docs/operations/clause-verifications/2026-05-17-aged-care-legislation-schads-in-charge-status.md`
 *  - `docs/operations/clause-verifications/2026-05-17-aged-care-legislation-schads-higher-duties-shape.md`
 */

/**
 * Canonical allowance codes emitted by Statura engines from Phase 1N onward.
 * All codes use the `_ALLOWANCE` suffix and are cross-award-neutral.
 *
 * Engine emission sites (post-Phase-1N):
 *   - Aged Care: `packages/utils/src/award-engines/aged-care.ts` calculate()
 *     + calculateWithOvertime()
 *   - Nurses: `packages/utils/src/award-engines/nurses.ts` calculate()
 *   - HPSS: `packages/utils/src/award-engines/hpss.ts` calculate()
 *   - SCHADS legacy: `packages/utils/src/schads.ts` buildAllowanceLines()
 */
export type CanonicalAllowanceCode =
  | 'SLEEPOVER_ALLOWANCE'
  | 'KM_ALLOWANCE'
  | 'KM_ALLOWANCE_EXEMPT'
  | 'KM_ALLOWANCE_TAXABLE'
  | 'UNIFORM_ALLOWANCE'
  | 'LAUNDRY_ALLOWANCE'
  | 'FIRST_AID_ALLOWANCE'
  | 'MEAL_ALLOWANCE'
  | 'MEAL_OT_ADDITIONAL_ALLOWANCE'
  | 'NAUSEOUS_WORK_ALLOWANCE'
  | 'BROKEN_SHIFT_ALLOWANCE' // Aged Care cl 22.7 — unified
  | 'BROKEN_SHIFT_SINGLE_ALLOWANCE' // SCHADS cl 20.12(a) — one break
  | 'BROKEN_SHIFT_MULTIPLE_ALLOWANCE' // SCHADS cl 20.12(b) — two breaks
  | 'ON_CALL_WEEKDAY_ALLOWANCE'
  | 'ON_CALL_WEEKEND_ALLOWANCE'
  | 'ON_CALL_SATURDAY_ALLOWANCE' // Nurses cl 17.2(a) — majority-of-shift weekend split
  | 'ON_CALL_SUNDAY_PH_ALLOWANCE' // Nurses cl 17.2(a) — majority-of-shift weekend split
  | 'LEAVE_LOADING_ALLOWANCE' // Phase 3 PR #3 sub-phase 3G — 17.5% loading per AC cl 28.4(a) / SCHADS cl 31.4(a) / Nurses cl 22.5(a) / HPSS cl 27.3(a) (Nurses + SCHADS + AC clauseRef specialist-verified 2026-05-19 Codex Pass A retrospective P2-N + P2-AC; HPSS citation primary-source verified 2026-09-14 — see FWC MA000027 clause 27.3 verification (2026-09-14))
  | 'REST_BREAK_TOP_UP_ALLOWANCE'
  | 'SLEEPOVER_FLANKING_MINIMUM_TOP_UP_ALLOWANCE'
  | 'MINIMUM_ENGAGEMENT_TOP_UP_ALLOWANCE'
  | 'DST_START_ORDINARY_TOP_UP_ALLOWANCE' // MZ-004 D5 — ordinary time lost to the DST-start clock change
  | 'PAID_MEAL_BREAK_ALLOWANCE';

/**
 * Bidirectional handler: translates ANY historical or current allowance code
 * to its canonical form. Returns the input unchanged if already canonical.
 *
 * For unmapped codes, returns the input as-is + emits a console warning
 * (silent-drop is the defect class Phase 1N closes — unmapped codes should
 * surface immediately, not vanish).
 *
 * Historical codes covered (per Phase 1N taxonomy memory deprecated-not-removed list):
 *  - SCHADS legacy: SLEEPOVER, BROKEN_SHIFT, TRAVEL_KM, DELAYED_MEAL,
 *    FIRST_AID, UNIFORM, LAUNDRY, ON_CALL_WEEKDAY, ON_CALL_WEEKEND
 *  - Aged Care legacy: SLEEPOVER, KM, UNIFORM_PER_SHIFT, LAUNDRY_PER_SHIFT,
 *    FIRST_AID, BROKEN_SHIFT, NAUSEOUS_WORK, MEAL, MEAL_OT_ADDITIONAL
 *  - Nurses legacy: KM, ON_CALL_WEEKDAY, ON_CALL_WEEKEND, ON_CALL_SATURDAY,
 *    ON_CALL_SUNDAY_PH, MEAL, UNIFORM_PER_SHIFT, LAUNDRY_PER_SHIFT
 *  - HPSS legacy: KM, UNIFORM_PER_SHIFT, LAUNDRY_PER_SHIFT, ON_CALL_WEEKDAY,
 *    ON_CALL_WEEKEND, MEAL
 *  - Live DB historical anomalies: SCHADS_SLEEPOVER_ALLOW (1 row),
 *    SCHADS_BROKEN_SHIFT (1 row — bias to MULTIPLE per taxonomy memory
 *    recommendation)
 *
 * BROKEN_SHIFT ambiguity note: historical SCHADS `BROKEN_SHIFT` (from before
 * the Phase 2.2 split into BROKEN_SHIFT_1/_2) is mapped to
 * BROKEN_SHIFT_MULTIPLE_ALLOWANCE as a CONSERVATIVE bias — preferring
 * over-pay on legacy reads rather than under-pay. Aged Care `BROKEN_SHIFT`
 * (cl 22.7 unified) maps to `BROKEN_SHIFT_ALLOWANCE`. The handler cannot
 * distinguish award context from the code alone, so it picks the more
 * generous interpretation. With 1 row of demo data in live DB this is a
 * theoretical concern.
 */
export function canonicalAllowanceCode(rawCode: string): CanonicalAllowanceCode | string {
  switch (rawCode) {
    // Already canonical → return as-is
    case 'SLEEPOVER_ALLOWANCE':
    case 'KM_ALLOWANCE':
    case 'KM_ALLOWANCE_EXEMPT':
    case 'KM_ALLOWANCE_TAXABLE':
    case 'UNIFORM_ALLOWANCE':
    case 'LAUNDRY_ALLOWANCE':
    case 'FIRST_AID_ALLOWANCE':
    case 'MEAL_ALLOWANCE':
    case 'MEAL_OT_ADDITIONAL_ALLOWANCE':
    case 'NAUSEOUS_WORK_ALLOWANCE':
    case 'BROKEN_SHIFT_ALLOWANCE':
    case 'BROKEN_SHIFT_SINGLE_ALLOWANCE':
    case 'BROKEN_SHIFT_MULTIPLE_ALLOWANCE':
    case 'ON_CALL_WEEKDAY_ALLOWANCE':
    case 'ON_CALL_WEEKEND_ALLOWANCE':
    case 'ON_CALL_SATURDAY_ALLOWANCE':
    case 'ON_CALL_SUNDAY_PH_ALLOWANCE':
    case 'LEAVE_LOADING_ALLOWANCE':
    case 'REST_BREAK_TOP_UP_ALLOWANCE':
    case 'SLEEPOVER_FLANKING_MINIMUM_TOP_UP_ALLOWANCE':
    case 'MINIMUM_ENGAGEMENT_TOP_UP_ALLOWANCE':
    case 'DST_START_ORDINARY_TOP_UP_ALLOWANCE':
    case 'PAID_MEAL_BREAK_ALLOWANCE':
      return rawCode;

    // Historical codes from current-codebase engines pre-Phase-1N
    case 'SLEEPOVER':
    case 'SCHADS_SLEEPOVER_ALLOW': // live DB anomaly (1 row)
      return 'SLEEPOVER_ALLOWANCE';
    case 'KM':
    case 'TRAVEL_KM':
      return 'KM_ALLOWANCE';
    case 'UNIFORM':
    case 'UNIFORM_PER_SHIFT':
      return 'UNIFORM_ALLOWANCE';
    case 'LAUNDRY':
    case 'LAUNDRY_PER_SHIFT':
      return 'LAUNDRY_ALLOWANCE';
    case 'FIRST_AID':
    case 'FIRST_AID_PT_HOURLY': // SCHADS Xero map alias (never engine-emitted)
      return 'FIRST_AID_ALLOWANCE';
    case 'MEAL':
    case 'DELAYED_MEAL':
      return 'MEAL_ALLOWANCE';
    case 'MEAL_OT_ADDITIONAL':
      return 'MEAL_OT_ADDITIONAL_ALLOWANCE';
    case 'NAUSEOUS_WORK':
      return 'NAUSEOUS_WORK_ALLOWANCE';
    case 'BROKEN_SHIFT':
    case 'SCHADS_BROKEN_SHIFT': // live DB anomaly (1 row) — see ambiguity note above
      // Conservative bias: SCHADS unified historical maps to MULTIPLE (higher
      // tier — avoid under-pay on legacy reads). Aged Care unified would also
      // route here; AC's cl 22.7 amount happens to align with the SCHADS
      // MULTIPLE tier in current rates, so the bias is benign for AC reads.
      // With 1 row of demo data in live DB this is a theoretical concern.
      return 'BROKEN_SHIFT_MULTIPLE_ALLOWANCE';
    case 'ON_CALL_WEEKDAY':
    case 'ON_CALL_WD':
      return 'ON_CALL_WEEKDAY_ALLOWANCE';
    case 'ON_CALL_WEEKEND':
    case 'ON_CALL_WE':
      return 'ON_CALL_WEEKEND_ALLOWANCE';
    case 'ON_CALL_SATURDAY':
      return 'ON_CALL_SATURDAY_ALLOWANCE';
    case 'ON_CALL_SUNDAY_PH':
      return 'ON_CALL_SUNDAY_PH_ALLOWANCE';
    case 'LEAVE_LOADING':
      // Pre-3G legacy code from older Xero map / pay-run-level computations.
      // Engines now emit canonical 'LEAVE_LOADING_ALLOWANCE' on
      // paid_annual_leave segments per Phase 3 PR #3 sub-phase 3G.
      return 'LEAVE_LOADING_ALLOWANCE';

    default:
      // Unmapped code — return as-is + warn. Phase 1N closes the silent-drop
      // defect class, so unmapped codes should surface (not vanish). A
      // genuinely-new emission from a future engine version that lands
      // without updating this handler will show up in logs/CI rather than
      // silently disappear from Xero exports.

      console.warn(
        `[allowance-code-canonical] Unmapped allowance code: '${rawCode}'. Returning as-is. Add to canonicalAllowanceCode() in packages/utils/src/allowance-code-canonical.ts per project_allowance_code_naming_taxonomy.md.`,
      );
      return rawCode;
  }
}

/**
 * Type guard — is the given string a canonical allowance code?
 */
export function isCanonicalAllowanceCode(code: string): code is CanonicalAllowanceCode {
  return canonicalAllowanceCode(code) === code;
}
